import type { TourStep } from '../types';
import { finishStep } from './common';

/**
 * Мастер показывает один шаг за раз, поэтому тур собирается из того, что на экране:
 * на выборе способа — карточки, на шаге 1 — поля темы и аудитории и т. д.
 */
export const layoutCreateSteps: TourStep[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Новое задание',
    content: 'Тур показывает то, что сейчас на экране. На каждом шаге мастера его можно запустить снова — «Помощь» → «Тур по этому экрану».',
  },
  {
    target: '[data-tour="lg-resume"]',
    title: 'Незапущенный черновик',
    content: '«Продолжить» вернёт начатое задание. После возврата проверьте шаг 2: тон, стиль и разнообразие запоминаются только при запуске.',
  },
  {
    target: '[data-tour="lg-method-ai14"]',
    title: 'Студия — 18',
    content: 'Рекомендуемый способ. ИИ предложит три направления, нарисует круги, топперы и капкейки и соберёт 18 макетов. Вы принимаете решения в контрольных точках.',
  },
  {
    target: '[data-tour="lg-method-prompts14"]',
    title: 'По промптам',
    content: 'ИИ пишет промпты, картинки вы рисуете в своём инструменте и загружаете. Платные только текстовые шаги.',
  },
  {
    target: '[data-tour="lg-method-images14"]',
    title: 'Студия по картинкам',
    content: 'Элементы уже нарисованы — загрузите их, система соберёт макеты; сколько их будет, зависит от числа картинок. Без ИИ и бесплатно.',
  },
  {
    target: '[data-tour="lg-method-duplicate"]',
    title: 'Дублирование макетов',
    content: 'Варианты уже существующей картинки: близкие копии или новый макет в той же теме.',
  },
  {
    target: '[data-tour="lg-bulk"]',
    title: 'Пачка заданий',
    content: 'Черновики сразу по списку тем с общим составом и лимитом.',
  },
  {
    target: 'nav[aria-label="Шаги формы"]',
    title: 'Три шага',
    content: '«О чём серия» — факты о товаре и покупателе. «Какой будет серия» — разнообразие, смелость, тон. «Проверка и запуск». Шаги открываются в любом порядке, «Требует внимания» — там есть что заполнить.',
  },
  {
    target: '[data-tour="lg-draft-badge"]',
    title: 'Черновик сохраняется сам',
    content: 'Поля шага 1, надписи и состав сохраняются на сервере через секунду после правки.',
  },
  {
    target: '[aria-label="Тематика и аудитория"]',
    title: 'О чём серия',
    content: 'Тематика или бренд, категория, повод, для кого. Бренд передаёт франшизу ИИ целиком; без бренда ИИ рисует только оригинальных персонажей.',
  },
  {
    target: '#catalog-brief',
    title: 'Краткое ТЗ',
    content: 'Главный ориентир для ИИ: настроение, мотивы, что обязательно и чего не надо. Пишите конкретно: «пастельные тона, без страшных персонажей».',
  },
  {
    target: '[aria-label="Заполнено автоматически"]',
    title: 'Проверьте автозаполнение',
    content: 'Статус получателя и персона подставились по полу и возрасту. Поправьте, если не подходят — эти поля попадут в каталог.',
  },
  {
    target: '[role="radiogroup"][aria-label="Разнообразие серии"]',
    title: 'Разнообразие серии',
    content: '«Единая» — один герой и стиль на всю серию. «Ассорти» — 2–3 ветки под одной идеей. «Каждый круг свой» — шесть разных кругов.',
  },
  {
    target: '[aria-label="Смелость идей"]',
    title: 'Смелость идей',
    content: 'От «Классики» (предсказуемо) до «Эксперимента» (праздник как афиша или комикс). По умолчанию «Интересно» — один неожиданный поворот в каждом направлении.',
  },
  {
    target: '[aria-label="Что решает ИИ"]',
    title: 'Что решает ИИ',
    content: 'Тон, стиль, возраст на макете, увлечения, персонаж, стоп-лист. По умолчанию решает ИИ; «Задать» забирает решение себе, «Вернуть ИИ» — отдаёт обратно.',
  },
  {
    target: '[aria-label="Надписи на макетах"]',
    title: 'Надписи',
    content: 'Поздравления повода, шутки, стёб или без надписей. «Точные надписи по типам» печатаются дословно — для имён и возраста.',
  },
  {
    target: '[data-tour="lg-no-repeat"]',
    title: 'Без повторов',
    content: 'ИИ видит героев и стили прошлых серий этого повода и не повторяет их.',
  },
  {
    target: '[role="status"][aria-label="Задание одной фразой"]',
    title: 'Задание одной фразой',
    content: 'Так задачу получит ИИ. Если фраза звучит не так, как задумано, правьте блок ссылкой «Изменить».',
  },
  {
    target: '[aria-label="Настройки запуска"]',
    title: 'Настройки запуска',
    content: 'Быстрый или пакетный режим (−50% на листах, до суток), проверка «Быстро» или «Пошагово», эскизы сразу или по кнопке.',
  },
  {
    target: '[data-summary-block="Модели и лимиты"]',
    title: 'Модели и лимит',
    content: 'Модели ИИ по ролям и лимит расхода на задание (по умолчанию 5 $). Перед каждым платным шагом экран покажет цену.',
  },
  {
    target: '[data-tour="lg-summary"]',
    title: 'Что получится',
    content: 'Состав серии, расходы и где понадобится человек — обновляется по ходу заполнения.',
  },
  {
    target: '[data-tour="lg-problem"]',
    title: 'Почему нельзя запустить',
    content: 'Первая незаполненная вещь и ссылка прямо к ней. Исправьте — подсказка сменится или кнопка станет активной.',
  },
  {
    target: '[data-tour="lg-next"]',
    title: 'Дальше',
    content: 'Следующий шаг мастера. «Запустить серию» рядом доступна, когда всё заполнено.',
  },
  {
    target: '[data-tour="lg-launch"]',
    title: 'Запустить серию',
    content: 'Запуск бесплатен — первый платный шаг это направления. Откроется студия: через несколько минут там появятся три направления с эскизами.',
  },
  finishStep,
];
